import { useFirebaseUser } from '@/features/Auth/identity/useFirebaseUser'
import { hasStatus } from '@/services/api/errorStatus'
import { queryClient } from '@/services/api/queryClient'
import { getFirebase } from '@/services/firebaseApp'
import { Profile } from '@/types'
import { ReactQuery } from '@codeleap/common'
import { useIsMutating, useMutation, useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { QueryKeys } from './queryKeys'
import { create, retrieve, update } from './views'

type UseProfileOptions = ReactQuery.UseQueryOptions<Profile>

/** Attempts before a 403 is taken as final. Mirrors mobile's `useProfile`. */
const RETRY_COUNT = 5

/**
 * One discard at a time, across every component that calls this hook.
 *
 * Module scope, not a ref: the whole point of this rewrite is that the decision belongs to
 * the query, which is shared, and not to whichever component happens to own the observer.
 */
let discarding = false

async function discardSession() {
  const firebase = await getFirebase()

  await firebase.auth().signOut()

  queryClient.client.clear()
}

export const useProfile = (options: UseProfileOptions = {} as UseProfileOptions) => {
  const fbUser = useFirebaseUser()

  const createIsMutating = useIsMutating({ mutationKey: QueryKeys.create.key, status: 'pending' })
  const isCreatingProfile = createIsMutating > 0

  const profile = useQuery({
    ...options,
    queryKey: QueryKeys.me.key(),
    queryFn: retrieve,
    refetchOnWindowFocus: false,
    refetchOnMount(q) {
      return q.state.dataUpdateCount == 0
    },
    retry: failureCount => failureCount < RETRY_COUNT - 1,
    enabled: !!fbUser?.uid && !isCreatingProfile
  })

  // A Firebase session can outlive the server's willingness to accept it — a retired
  // identity, a profile this environment has never seen, a token minted against another
  // project. Nothing about that resolves itself, and `refetchOnMount` re-fires on every
  // screen that mounts, so without this the app sits in an endless 403 loop with the person
  // neither signed in nor signed out.
  //
  // Read off `isError`, which is state of the QUERY. An earlier version counted attempts in a
  // `useRef` inside `queryFn`, mirroring mobile — and it never fired: the ref belongs to one
  // component, every caller of this hook has its own, and a navigation mid-retry unmounts the
  // observer and starts the count again from zero. Mobile almost certainly has the same dead
  // guard for the same reason.
  //
  // The status comes from `getErrorStatus`, not `error.response.status`: the client rejects with
  // an `APIError` wrapper, so the obvious read is always `undefined` and this guard sat dead a
  // second time — the retries were spent, the loop went on, and nothing ever signed anyone out.
  //
  // Only after the retries are spent, and only on a 403: a 5xx or a dropped connection says
  // the lookup broke, not that the identity is unusable, and signing people out over a blip
  // is the mistake this guard exists to avoid.
  const sessionIsUnusable = profile.isError && hasStatus(profile.error, [403]) && !!fbUser?.uid && !isCreatingProfile

  useEffect(() => {
    if (!sessionIsUnusable || discarding) return

    discarding = true

    discardSession().finally(() => {
      discarding = false
    })
  }, [sessionIsUnusable])

  return profile
}

export const useCreateProfile = () => {
  const createProfile = useMutation({
    mutationKey: QueryKeys.create.key,
    mutationFn: create,
    onSuccess: () => {
      QueryKeys.me.refresh()
    }
  })

  return createProfile
}

export const useUpdateProfile = () => {
  const updateProfile = useMutation({
    mutationKey: QueryKeys.create.key,
    mutationFn: update,
    onSuccess: () => {
      QueryKeys.me.refresh()
    }
  })

  return updateProfile
}
